import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { apiService, AnalyticsSummary } from '../../services/apiService';
import { StatCard } from '../components/StatCard';
import { StatusBadge } from '../components/StatusBadge';
import { adminTheme, statusColors } from '../styles/adminTheme';

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
  margin-bottom: 20px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  padding: 20px;
`;

const CardTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${adminTheme.colors.text};
  margin-bottom: 16px;
`;

const RfqRow = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${adminTheme.colors.border};
  font-size: 13px;
  cursor: pointer;
  gap: 12px;

  &:hover {
    background: ${adminTheme.colors.rowAlt};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const RfqName = styled.span`
  font-weight: 500;
  color: ${adminTheme.colors.text};
  flex: 1;
`;

const RfqCompany = styled.span`
  color: ${adminTheme.colors.textSecondary};
  flex: 1;
`;

const RfqDate = styled.span`
  color: ${adminTheme.colors.textMuted};
  font-size: 11px;
`;

const CompSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
`;

const CompCard = styled.div`
  padding: 12px;
  background: ${adminTheme.colors.rowAlt};
  border-radius: 6px;
  text-align: center;
`;

const CompCount = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${adminTheme.colors.text};
`;

const CompLabel = styled.div`
  font-size: 11px;
  color: ${adminTheme.colors.textSecondary};
  margin-top: 2px;
`;

const CompCurves = styled.div`
  font-size: 10px;
  color: ${adminTheme.colors.textMuted};
  margin-top: 2px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: ${adminTheme.colors.textMuted};
`;

const typeLabels: Record<string, string> = {
  pump: 'Pumps',
  pipe: 'Pipes',
  branch: 'Branches',
  merge: 'Merges',
  restriction: 'Restrictions',
  heat_source: 'Heat Sources',
  cooling_package: 'Cooling Pkgs',
};

export const DashboardPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    apiService.getAnalyticsSummary()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return <Loading>Loading dashboard...</Loading>;

  const pipelineData = Object.entries(data.rfqs_by_status).map(([status, count]) => ({
    status,
    count,
    fill: statusColors[status]?.text || '#5f6368',
  }));

  return (
    <div>
      <PageTitle>Dashboard</PageTitle>

      <StatsRow>
        <StatCard value={data.total_rfqs} label="Total RFQs" />
        <StatCard value={data.total_simulations} label="Simulations" />
        <StatCard value={data.total_pdf_exports} label="PDF Exports" />
        <StatCard value={data.total_shared_circuits} label="Shared Circuits" />
      </StatsRow>

      <Grid>
        <Card>
          <CardTitle>RFQ Pipeline</CardTitle>
          {pipelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={pipelineData}>
                <CartesianGrid stroke="#f1f3f4" strokeDasharray="4 4" />
                <XAxis dataKey="status" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12 }} />
                <Bar dataKey="count" fill={adminTheme.colors.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ color: adminTheme.colors.textMuted, fontSize: 13 }}>No RFQs yet</div>
          )}
        </Card>

        <Card>
          <CardTitle>Component Library</CardTitle>
          <CompSummaryGrid>
            {Object.entries(data.component_counts).map(([type, count]) => (
              <CompCard key={type}>
                <CompCount>{count}</CompCount>
                <CompLabel>{typeLabels[type] || type}</CompLabel>
                <CompCurves>
                  {data.components_with_curves[type] || 0} with curves
                </CompCurves>
              </CompCard>
            ))}
          </CompSummaryGrid>
        </Card>
      </Grid>

      <Card>
        <CardTitle>Recent RFQs</CardTitle>
        {data.recent_rfqs.length === 0 ? (
          <div style={{ color: adminTheme.colors.textMuted, fontSize: 13, padding: '12px 0' }}>
            No RFQs submitted yet
          </div>
        ) : (
          data.recent_rfqs.map(rfq => (
            <RfqRow key={rfq.id} onClick={() => navigate(`/panel/rfq/${rfq.id}`)}>
              <RfqName>{rfq.name}</RfqName>
              <RfqCompany>{rfq.company}</RfqCompany>
              <StatusBadge status={rfq.status} />
              <RfqDate>{new Date(rfq.created_at).toLocaleDateString()}</RfqDate>
            </RfqRow>
          ))
        )}
      </Card>
    </div>
  );
};
